import { Box, Fade, Skeleton } from '@mui/material';
import { ReactNode, Suspense, useEffect, useState } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useLazyLoadPerformance } from '../hooks/useLazyLoadPerformance';

interface AdvancedLazyLoadSectionProps {
  children: ReactNode;
  height?: string | number;
  threshold?: number;
  rootMargin?: string;
  preload?: boolean; // Whether to preload when getting close
  loadingDelay?: number; // Delay before showing loading state
  sectionName?: string; // For performance tracking
}

const AdvancedLazyLoadSection = ({ 
  children, 
  height = '400px', 
  threshold = 0.1,
  rootMargin = '50px',
  preload = true,
  loadingDelay = 100,
  sectionName = 'Unknown Section'
}: AdvancedLazyLoadSectionProps) => {
  const { elementRef, isIntersecting, hasIntersected } = useIntersectionObserver({
    threshold,
    rootMargin: preload ? '200px' : rootMargin,
  });
  
  const { recordIntersection, recordLoad } = useLazyLoadPerformance(sectionName);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (hasIntersected) {
      recordIntersection();
      setShouldLoad(true);
    }
  }, [hasIntersected, recordIntersection]);

  useEffect(() => {
    if (isIntersecting && !shouldLoad) {
      const timer = setTimeout(() => {
        setShowLoading(true);
      }, loadingDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isIntersecting, shouldLoad, loadingDelay]);

  useEffect(() => {
    if (shouldLoad) {
      // Record load time after a small delay to ensure component is rendered
      const timer = setTimeout(() => {
        recordLoad();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shouldLoad, recordLoad]);

  const LoadingSkeleton = () => (
    <Box sx={{ 
      width: '100%', 
      height, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      borderRadius: 2,
      overflow: 'hidden'
    }}>
      <Skeleton 
        variant="rectangular" 
        width="100%" 
        height="100%" 
        animation="wave"
        sx={{ borderRadius: 2 }}
      />
    </Box>
  );

  return (
    <Box ref={elementRef}>
      {shouldLoad ? (
        <Fade in={true} timeout={500}>
          <Box>
            <Suspense fallback={<LoadingSkeleton />}>
              {children}
            </Suspense>
          </Box>
        </Fade>
      ) : (
        <Fade in={showLoading} timeout={300}>
          <Box>
            <LoadingSkeleton />
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default AdvancedLazyLoadSection; 